import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { toast } from 'sonner';
import { Loader2, CreditCard as CreditCardIcon, Shield, ExternalLink, CheckCircle } from 'lucide-react';
import { API } from '../App';
import axios from 'axios';

/**
 * Square Payment Form Component
 * Redirects user to Square's hosted checkout page for payment
 */
const SquarePaymentForm = ({ 
  amount, 
  planName, 
  billingPeriod,
  squarePlanIdOverride,
  isLaunchOffer,
  onSuccess, 
  onCancel,
  userEmail,
  userName 
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handlePayment = async () => {
    setIsProcessing(true);

    try {
      const squarePlanId = squarePlanIdOverride
        ? squarePlanIdOverride
        : (billingPeriod === 'annual'
          ? (isLaunchOffer ? 'club_annual_launch' : 'club_annual')
          : 'club_monthly');
      
      const response = await axios.post(`${API}/square/create-checkout`, {
        plan_id: squarePlanId,
        success_url: `${window.location.origin}/dashboard?success=true&payment=square`,
        cancel_url: `${window.location.origin}/pricing`
      }, { withCredentials: true });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Erreur lors de la création du paiement');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du traitement du paiement');
      setIsProcessing(false);
    }
  };

  const features = [
    'Agent IA Immobilier',
    'Statistiques IA avancées',
    'Messagerie privée',
    'Annuaire des membres',
    'Podcasts exclusifs',
    'Publication de biens',
    'Support prioritaire'
  ];

  return (
    <Card className="w-full max-w-md mx-auto border-slate-200 shadow-xl bg-white">
      <CardHeader className="text-center border-b pb-4">
        <CardTitle className="text-xl font-bold text-slate-900 flex items-center justify-center gap-2">
          <CreditCardIcon className="h-5 w-5 text-[#0D47A1]" />
          Confirmer votre abonnement
        </CardTitle>
        <CardDescription>
          {planName}{billingPeriod === 'annual' && !squarePlanIdOverride ? ' - Annuel' : ''}{billingPeriod === 'monthly' && !squarePlanIdOverride ? ' - Mensuel' : ''}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="pt-6">
        <div className="text-center mb-6 p-4 bg-gradient-to-br from-[#0D47A1]/5 to-[#0D47A1]/10 rounded-xl">
          <p className="text-sm text-slate-500 mb-1">Montant à payer</p>
          <p className="text-4xl font-bold text-[#0D47A1]">
            {amount.toFixed(2)} €
          </p>
          <p className="text-sm text-slate-500 mt-1">
            {squarePlanIdOverride === 'club_annual_4x' ? 'par prélèvement (4 fois)' : (billingPeriod === 'annual' ? 'pour 12 mois' : 'par mois')}
          </p>
        </div>

        <div className="mb-6">
          <p className="text-sm font-medium text-slate-700 mb-3">Inclus dans votre abonnement :</p>
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-3">
          <Button
            className="w-full bg-[#0D47A1] hover:bg-[#0A367A] h-12 text-base"
            onClick={handlePayment}
            disabled={isProcessing}
            data-testid="square-payment-button"
          >
            {isProcessing ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                Redirection vers Square...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Payer {amount.toFixed(2)} €
                <ExternalLink className="h-4 w-4" />
              </span>
            )}
          </Button>

          <Button
            variant="outline"
            className="w-full"
            onClick={onCancel}
            disabled={isProcessing}
            data-testid="cancel-payment-button"
          >
            Annuler
          </Button>
        </div>

        <div className="mt-6 p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Shield className="h-4 w-4 text-green-600" />
            <span>Paiement sécurisé via Square. Vous serez redirigé vers une page de paiement sécurisée.</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SquarePaymentForm;
